import ky, { HTTPError } from 'ky'
import { signOut } from 'aws-amplify/auth'
import { useAuthStore } from '@/stores/authStore'
import { env } from '@/lib/env'
import { captureError, isExpectedProductFlowError } from '@/lib/errorReporting'

/**
 * Central place to report/log request errors (O3). Always logs to the console
 * (dev behavior unchanged). Additionally captures to Sentry when initialized —
 * but FILTERS OUT expected product-flow 4xx responses (403 tier_upgrade_required,
 * 429 preview_limit_exhausted, 401 refresh-handled) so Sentry sees only
 * 5xx / network / unexpected errors. No-op to Sentry when VITE_SENTRY_DSN unset.
 */
function reportRequestError(error: unknown, context: string): void {
  console.error(`[api] ${context}:`, error)
  if (!isExpectedProductFlowError(error)) {
    captureError(error, context)
  }
}

/**
 * Shared in-flight refresh promise so simultaneous 401s trigger at most one
 * refresh. The first caller kicks off the refresh; concurrent callers await
 * the same promise instead of stampeding the token endpoint.
 */
let refreshInFlight: Promise<void> | null = null

function refreshOnce(): Promise<void> {
  if (!refreshInFlight) {
    refreshInFlight = useAuthStore
      .getState()
      .refreshSession()
      .finally(() => {
        refreshInFlight = null
      })
  }
  return refreshInFlight
}

function redirectToLogin(): void {
  try {
    void signOut().catch(() => {})
  } catch {
    /* ignore */
  }
  useAuthStore.getState().logout()
  window.location.href = '/auth/login'
}

export const api = ky.create({
  prefixUrl: env.VITE_API_BASE_URL,
  // Fail fast rather than hanging forever on a stalled connection.
  timeout: 30_000,
  // Bounded retry for idempotent GETs only; ky backs off automatically.
  retry: {
    limit: 2,
    methods: ['get'],
    statusCodes: [408, 429, 500, 502, 503, 504],
  },
  hooks: {
    beforeRequest: [
      async (request) => {
        const { tokens } = useAuthStore.getState()
        if (tokens && tokens.expiresAt < Date.now() + 60_000) {
          await refreshOnce()
        }
        const currentTokens = useAuthStore.getState().tokens
        if (currentTokens) {
          request.headers.set('Authorization', `Bearer ${currentTokens.accessToken}`)
        }
      },
    ],
    afterResponse: [
      async (request, options, response) => {
        if (response.status !== 401) {
          return response
        }

        // Avoid retry loops: only attempt a refresh-then-retry once per request.
        if (request.headers.get('x-refresh-retry') === '1') {
          redirectToLogin()
          return response
        }

        // Refresh-then-retry once. Concurrent 401s share a single refresh via
        // refreshOnce(); only redirect to login if the refresh fails to yield
        // a usable token.
        try {
          await refreshOnce()
        } catch (err) {
          reportRequestError(err, 'token refresh failed')
          redirectToLogin()
          return response
        }

        const refreshedTokens = useAuthStore.getState().tokens
        if (!refreshedTokens) {
          redirectToLogin()
          return response
        }

        const retryRequest = new Request(request, {
          headers: new Headers(request.headers),
        })
        retryRequest.headers.set('Authorization', `Bearer ${refreshedTokens.accessToken}`)
        retryRequest.headers.set('x-refresh-retry', '1')
        return ky(retryRequest, { ...options, retry: 0 })
      },
    ],
    beforeError: [
      (error) => {
        reportRequestError(error, 'request failed')
        return error
      },
    ],
  },
})

// Anonymous client — no auth beforeRequest hook, so unauthenticated public
// visitors are never blocked by a stale/missing token. Mirrors the authed
// client's GET-only retry + timeout.
export const anonApi = ky.create({
  prefixUrl: env.VITE_API_BASE_URL,
  timeout: 30_000,
  retry: { limit: 2, methods: ['get'], statusCodes: [408, 429, 500, 502, 503, 504] },
})

export { HTTPError }
